import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CookieHandlerService } from './cookie.service';

// Attaches the verified consumer identity JWT to requests going to our own
// backend services (queries/rag/tts) — never to third-party URLs, which
// would otherwise leak the token. Read synchronously from the cookie rather
// than UserStoreService, since this must run on every request including
// ones issued before login state has settled (e.g. getUserByToken itself).
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private cookie: CookieHandlerService;

  constructor(cookie: CookieHandlerService) {
    this.cookie = cookie;
  }

  // ragApiURL is the '/ingest' endpoint itself (other rag routes are built by
  // string-replacing its path suffix elsewhere in the app), so origins are
  // compared rather than raw string prefixes — startsWith would miss
  // /upload-doc and /check-doc-url otherwise. doBaseFunctionsURL covers the
  // DO Serverless functions called directly from the browser (paypal,
  // security, del-bucket-file, upload-image, presigned-url, img-gen,
  // lifecycle-emails, meta-event) — several of these now require this same
  // JWT (see DO-Serverless/.../lib/auth.js requireInternalOrConsumer);
  // meta-event fires for anonymous visitors too, which is fine since this
  // interceptor just skips attaching a header when there's no token yet.
  // NOTE (2026-09-28): the /generate-imagined-title caller mentioned below has
  // been commented out (unused, see business-admin.service.ts); the comment is
  // kept until that method is removed for good.
  // openaiChatApiURL is chat's origin — used both for the Socket.io
  // connection (auth handled separately via the handshake `auth` option, see
  // chat.service.ts) and for chat's plain HTTP routes like
  // /generate-imagined-title, which DO go through HttpClient and need this
  // interceptor. Missing this was a real bug (2026-07-14): that route 401'd
  // for every caller since no Authorization header was ever attached to it.
  private ownOrigins = [environment.dbBaseUrl, environment.ragApiURL, environment.speechApiURL, environment.doBaseFunctionsURL, environment.openaiChatApiURL]
    .filter(Boolean)
    .map(base => {
      try { return new URL(base).origin; } catch { return null; }
    })
    .filter((origin): origin is string => !!origin);

  private isOwnBackend(url: string): boolean {
    try {
      return this.ownOrigins.includes(new URL(url, window.location.origin).origin);
    } catch {
      return false;
    }
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isOwnBackend(req.url)) {
      return next.handle(req);
    }

    const token = this.cookie.getTokenSync();
    if (!token) {
      return next.handle(req);
    }

    return next.handle(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }));
  }
}
